// import { GetTokenSilentlyOptions, RedirectLoginOptions } from '@auth0/auth0-spa-js';
import axios from '../../services/common/axios';
import jwt_decode from 'jwt-decode';
import config from '../../configs/config';
import {
	GET_AUTH_DATA_CHANNEL,
	IS_GETTING_TOKEN_CHANNEL,
	LOGOUT_CHANNEL,
	SET_AUTH_DATA_CHANNEL,
	REMOVE_AUTH_DATA_CHANNEL,
} from '../broadcast';

interface IAuthData {
	id: string;
	info: IUserInfo;
}

interface IUserInfo {
	UserId: string;
	ApiKey: string;
	Company: string;
	Email: string;
	TeamMemberId: string;
	TeamMemberEmail: string;
	LogoutUrl?: string;
	Name: string;
	Tokens: {
		IdToken: string | null;
		AccessToken: string;
		RefreshToken: string;
	};
	Tenant: {
		IdentityOrganizationId: string;
		IdentityUserId: string;
		InventoryTenantId: string;
	};
	IsClient: boolean;
	ParentUserId: string | null;
	RegisteredOn: string;
	IsSendAccount: boolean;
}

interface IDecodedSendData {
	'https://auth.sitecorecloud.io/claims/tenant_id': string;
	'https://auth.sitecorecloud.io/claims/tenant_name': string;
	'https://auth.sitecorecloud.io/claims/org_id': string;
	'https://auth.sitecorecloud.io/claims/tenant/cdp_client_key': string;
	'https://auth.sitecorecloud.io/claims/org_name': string;
	'https://auth.sitecorecloud.io/claims/org_display_name': string;
	'https://auth.sitecorecloud.io/claims/org_type': string;
	'https://auth.sitecorecloud.io/claims/email': string;
	'https://auth.sitecorecloud.io/claims/roles': string[];
	'https://auth.sitecorecloud.io/claims/client_name': string;
	iss: string;
	sub: string;
	aud: string[];
	iat: number;
	exp: number;
	azp: string;
	scope: string;
	org_id: string;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

class Auth {
	private authData: IAuthData;
	private parentData: IAuthData | null;
	private setAuthBroadcast: BroadcastChannel;
	private getAuthBroadcast: BroadcastChannel;
	private removeAuthBroadcast: BroadcastChannel;
	private logoutBroadcast: BroadcastChannel;
	private tokenLockBroadcast: BroadcastChannel;
	private tokenLock: boolean = false;
	private isOldLogin: boolean = false;

	constructor() {
		this.setAuthBroadcast = new BroadcastChannel(SET_AUTH_DATA_CHANNEL);
		this.getAuthBroadcast = new BroadcastChannel(GET_AUTH_DATA_CHANNEL);
		this.removeAuthBroadcast = new BroadcastChannel(REMOVE_AUTH_DATA_CHANNEL);
		this.logoutBroadcast = new BroadcastChannel(LOGOUT_CHANNEL);
		this.tokenLockBroadcast = new BroadcastChannel(IS_GETTING_TOKEN_CHANNEL);
	}

	initAuthData(initApp: any) {
		this.tokenLockBroadcast.onmessage = (e) => {
			this.setTokenLock(e.data);
		};

		this.logoutBroadcast.onmessage = (e) => {
			this.redirectToLogin();
		};

		this.setAuthBroadcast.onmessage = (e) => {
			if (!this.authData) {
				this.setAuthData(e.data.authData);

				if (e.data.parentData) this.setParentData(e.data.parentData);
				else this.deleteParentData();

				this.setIsOldLogin(e.data.isOldLogin);
				this.setTokenLock(false);

				if (e.data.withInit) {
					initApp();
				}
			}
		};

		this.getAuthBroadcast.onmessage = (e) => {
			if (this.authData) {
				this.setAuthBroadcast.postMessage({
					authData: this.authData,
					withInit: true,
					parentData: this.parentData,
					isOldLogin: this.isOldLogin,
				});
			}
		};

		this.removeAuthBroadcast.onmessage = (e) => {
			if (this.authData) {
				this.redirectToLogin();
			}
		};

		let dataToBeStored: IAuthData | undefined;
		let parentDataToBeUsed: IAuthData | undefined;
		const sessionData = this.getSessionAuthData();
		const preservedUser = this.getStorageAuthData();
		const mooUser = localStorage.getItem('moo_user');
		const mooParent = localStorage.getItem('moo_parent');
		const preservedParent = this.getStorageParentData();

		if (sessionData) {
			dataToBeStored = sessionData;
		} else if (preservedUser) {
			dataToBeStored = preservedUser;
		} else if (mooUser && mooUser !== 'undefined') {
			this.isOldLogin = true;
			dataToBeStored = { id: '', info: JSON.parse(mooUser) };
		}

		if (mooParent || preservedParent) {
			parentDataToBeUsed = mooParent ? { id: '', info: JSON.parse(mooParent) } : preservedParent;
			if (parentDataToBeUsed) {
				this.setParentData(parentDataToBeUsed);
			}
		}

		if (dataToBeStored) {
			this.setAuthData(dataToBeStored);
			initApp();
		} else {
			// get by broadcast
			this.getAuthBroadcast.postMessage('Retrieve data');
		}

		this.removeSessionAuthData();
		this.removeStorageAuthData();

		setTimeout(() => {
			if (!this.authData) {
				this.redirectToLogin();
			}
		}, 2000);
	}

	// Get & Set
	getAuthData() {
		return this.authData;
	}

	setAuthData(data: IAuthData) {
		this.authData = data;
		// localStorage.setItem('moo_user', JSON.stringify(data.info));
	}

	removeAuthData() {
		this.authData = undefined as any;
		localStorage.removeItem('moo_user');
	}

	getParentData() {
		return this.parentData;
	}

	setParentData(data?: IAuthData) {
		if (data) {
			this.parentData = data;
			const existingMooParent = localStorage.getItem('moo_parent');
			if (!existingMooParent) {
				localStorage.setItem('moo_parent', JSON.stringify(data.info));
			}
		}
	}

	deleteParentData() {
		this.parentData = null;
		localStorage.removeItem('moo_parent');
	}

	setTokenLock(val: boolean) {
		this.tokenLock = val;
	}

	getIsOldLogin() {
		return this.isOldLogin;
	}

	setIsOldLogin(value: boolean | undefined) {
		if (value) {
			this.isOldLogin = value;
		}
	}

	waitForOtherTabs = async () => {
		if (this.tokenLock === false) {
			return true;
		}
		let i = 0;
		while (i < 20) {
			await sleep(250);
			if (this.tokenLock !== true) {
				break;
			}
			i++;
		}
		return true;
	};

	// Token Handlers, returns undefined if unable to refresh the token
	getToken = async (isIdToken: boolean = false) => {
		// THIS 'IF' HERE IS NECESSARY TO ACCOUNT FOR ASYNC EXECUTIONS OF CONCURRENT CALLS, FIXES THE TOKEN REFRESH WHEN THERE ARE CONCURRENT CALLS
		// REMOVING THE 'IF'(ALTHOUGH THE SAME 'IF' EXISTS IN waitForOtherTabs) WILL BREAK THIS FUNCTIONALITY
		// BECAUSE OF THE WAY THE BROWSER QUEUES ASYNC CALLBACKS WHEN CONCURRENT CALLS ARE MADE, THE FIRST CALL WOULD NOT BE ABLE TO SET THE LOCK IN TIME
		if (this.tokenLock === true) {
			await this.waitForOtherTabs();
		}

		let data = this.getAuthData();

		if (!data?.info?.Tokens) {
			return '';
		}

		if (!data) {
			this.redirectToLogin();
			return;
		}

		if (!this.checkTokenExpired(data.info.Tokens.AccessToken)) {
			if (isIdToken === true) {
				return data.info.Tokens.IdToken;
			}
			return data.info.Tokens.AccessToken;
		}

		try {
			this.setTokenLock(true);

			this.tokenLockBroadcast.postMessage(true);
			const tokensObj = {
				client_id: data.info.ApiKey,
				refresh_token: data.info.Tokens.RefreshToken,
			};

			const rsp = await axios.post(config.auth.base + config.auth.refresh, tokensObj);

			const Tokens = {
				AccessToken: rsp.data.access_token,
				IdToken: rsp.data.id_token,
				RefreshToken: rsp.data.refresh_token,
			};

			const newData = {
				...this.authData,
				info: {
					...this.authData.info,
					Tokens,
				},
			};

			this.setAuthData(newData);

			this.setTokenLock(false);
			this.setAuthBroadcast.postMessage({ authData: newData, parentData: this.parentData });
			if (isIdToken === true) {
				return rsp.data.id_token as string;
			}
			return rsp.data.access_token as string;
		} catch (e) {
			this.setTokenLock(false);
			this.tokenLockBroadcast.postMessage(false);

			if (e.response.status !== 200) {
				this.redirectToLogin();
			}

			return;
		}
	};

	getDecodedToken(): IDecodedSendData {
		return jwt_decode(this.authData.info.Tokens.AccessToken);
	}

	checkTokenExpired = (token: string) => {
		try {
			const tk: any = jwt_decode(token);

			const tokenUTCDate = tk.exp;
			// return Math.floor(Date.now() / 1000) > tokenUTCDate - 780;
			return Math.floor(Date.now() / 1000) > tokenUTCDate;
		} catch (e) {
			console.error(e);
			// TOKEN IS SHIT, DO SOMETHING
		}
	};

	// checkLogin = async () => {
	// 	try {
	// 		await this.authData.getTokenSilently();
	// 	} catch (error) {
	// 		if (error.error !== 'login_required') {
	// 			throw error;
	// 		}
	// 		await this.authData.loginWithRedirect();
	// 	}
	// };

	redirectToLogin = async () => {
		const redirect = () => {
			window.removeEventListener('beforeunload', this.preserveLocalData);
			this.removeStorageAuthData();
			this.removeAuthData();
			this.deleteParentData();
			this.removeAuthBroadcast.postMessage('Remove');
			const pathName = location.pathname === '/' ? '' : encodeURIComponent(location.pathname + location.search);
			location.href = location.origin + `/login${pathName ? '?returnTo=' + pathName : ''}`;
		};

		if (this.authData?.info?.LogoutUrl) {
			const logoutIFrame = document.createElement('iframe');
			logoutIFrame.width = '0px';
			logoutIFrame.height = '0px';
			logoutIFrame.src = this.authData.info.LogoutUrl;
			logoutIFrame.onload = () => {
				redirect();
			};
			document.body.append(logoutIFrame);
		} else {
			redirect();
		}
	};

	// Sessions
	getSessionAuthData = (): IAuthData | undefined => {
		const data = sessionStorage.getItem('session');
		// TODO - delete this
		if (data) {
			try {
				const parsedData = JSON.parse(data);
				return parsedData;
			} catch (e) {
				console.error(e);
			}
		}
	};

	removeSessionAuthData = () => {
		sessionStorage.removeItem('session');
	};

	// Local Storage
	setStorageAuthData = () => {
		sessionStorage.setItem('preserved_data', JSON.stringify({ user_data: this.getAuthData(), isOldLogin: this.isOldLogin }));
		sessionStorage.setItem('preserved_data_parent', JSON.stringify({ user_data: this.getParentData(), isOldLogin: this.isOldLogin }));
	};

	getStorageAuthData = (): IAuthData | undefined => {
		const data = sessionStorage.getItem('preserved_data');
		if (data !== 'undefined' && data) {
			try {
				const parsedData = JSON.parse(data);
				this.setIsOldLogin(parsedData.isOldLogin);
				if (parsedData.user_data) {
					return parsedData.user_data;
				}
			} catch (e) {
				console.error(e);
			}
		}
	};

	getStorageParentData = (): IAuthData | undefined => {
		const data = sessionStorage.getItem('preserved_data_parent');
		if (data !== 'undefined' && data) {
			try {
				const parsedData = JSON.parse(data);
				if (parsedData.user_data) {
					return parsedData.user_data;
				}
			} catch (e) {
				console.error(e);
			}
		}
	};

	removeStorageAuthData = () => {
		sessionStorage.removeItem('preserved_data');
		sessionStorage.removeItem('preserved_data_parent');
		localStorage.removeItem('moo_user');
		
		// localStorage.removeItem('moo_parent');
	};

	addStorageEventHandler = () => {
		window.addEventListener('beforeunload', this.preserveLocalData);
	};

	preserveLocalData = (e: any) => {
		e.stopImmediatePropagation();
		this.setStorageAuthData();
		localStorage.removeItem('moo_user');
	};

	broadcastAuthData = (data: { authData?: IAuthData; withInit?: boolean; parentData?: IAuthData; isOldLogin?: boolean }) => {
		this.setAuthBroadcast.postMessage(data);
	};
}

export default new Auth();
